import { useCallback, useMemo } from "react";

import { ModelStatusTypes, type Models } from "@prisma/client";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import { v4 as uuid } from "uuid";

import { FEATURE_ROUTES } from "@musicfy/components/Navigation/constants";
import { isRefusalError } from "@musicfy/libs/generationStatus";
import modalAxios, { ModalFunction } from "@musicfy/libs/modalAxios";
import { useModelsContext } from "@musicfy/libs/ModelsProvider";
import { useToastContext } from "@musicfy/libs/ToastProvider";
import { api } from "@musicfy/utils";
import { useStorage } from "@musicfy/utils/hooks";
import { Buckets, type IFileUpload } from "@musicfy/utils/hooks/useStorage";
import { pickUserUpdateModelFields } from "@musicfy/utils/userModelUpdateFields";

import { DEFAULT_MODEL, type IModel } from "../IModels";

export const useModels = () => {
  const { models, setModels } = useModelsContext();

  const { data } = useSession();

  const user = data?.user;

  const upsertModelMutation = api.models.upsertModel.useMutation();
  const updateModelMutation = api.models.updateModel.useMutation();
  const deleteModelMutation = api.models.deleteModel.useMutation();

  const createModel = useCallback(
    async (
      model: Partial<Models> & {
        id: string;
        userId: string;
        artist: string;
      }
    ): Promise<[IModel, IModel[]]> => {
      const newModel = await upsertModelMutation.mutateAsync({
        model: {
          ...DEFAULT_MODEL,
          ...model,
        },
      });

      let newModels: IModel[] = [];

      const existingModelIndex = models.findIndex(
        (model) => model.id === newModel.id
      );
      const existingModel = models[existingModelIndex];

      const modifiedModel = {
        ...newModel,
        // createModel only ever runs for the signed-in user's own model, so this is
        // always true. getModels computes the same flag server-side now that it no
        // longer returns userId.
        isOwner: true,
        isFavorited: existingModel?.isFavorited ?? false,
      };

      if (existingModel) {
        newModels = [...models];
        newModels[existingModelIndex] = modifiedModel;
      } else {
        newModels = [modifiedModel, ...models];
      }

      setModels(newModels);

      return [modifiedModel, newModels];
    },
    [upsertModelMutation, models, setModels]
  );

  const updateModel = useCallback(
    (modelId: string) => {
      return async (model: Partial<IModel>, upload = false): Promise<void> => {
        let previousPartial: Partial<IModel> | null = null;

        setModels((prevModels) => {
          const newModels = [...prevModels];
          const modelIndex = newModels.findIndex((m) => m.id === modelId);
          const currentModel = newModels[modelIndex];

          if (!currentModel) return newModels;

          const snapshot: Partial<IModel> = {};
          for (const key of Object.keys(model) as (keyof IModel)[]) {
            // Capture prior values so a failed upload can roll back optimistic UI.
            Object.assign(snapshot, { [key]: currentModel[key] });
          }
          previousPartial = snapshot;

          newModels[modelIndex] = {
            ...currentModel,
            ...model,
          };

          return newModels;
        });

        if (!upload) {
          return;
        }

        try {
          await updateModelMutation.mutateAsync({
            id: modelId,
            ...pickUserUpdateModelFields(model as Record<string, unknown>),
          });
        } catch (err) {
          if (previousPartial) {
            const rollback: Partial<IModel> = previousPartial;
            setModels((prevModels) => {
              const newModels = [...prevModels];
              const modelIndex = newModels.findIndex((m) => m.id === modelId);
              const currentModel = newModels[modelIndex];

              if (!currentModel) return newModels;

              newModels[modelIndex] = {
                ...currentModel,
                ...rollback,
              };

              return newModels;
            });
          }
          throw err;
        }
      };
    },
    [setModels, updateModelMutation]
  );

  const deleteModel = useCallback(
    async (modelId: string) => {
      const modelIndex = models.findIndex((model) => model.id === modelId);
      const model = models[modelIndex];

      if (!model || !user) return;

      setModels((prevModels) => {
        const newModels = [...prevModels];

        newModels.splice(modelIndex, 1);

        return newModels;
      });

      let modalToken: string | undefined;
      try {
        const result = await deleteModelMutation.mutateAsync({ modelId });
        modalToken = result.modalToken;
      } catch (err) {
        console.log(err);
      }
      if (model.status !== ModelStatusTypes.completed) {
        return;
      }
      try {
        await modalAxios(ModalFunction.DELETE_MODEL, modalToken).post("", {
          user_id: user.id,
          model_id: modelId,
        });
      } catch (err) {
        console.log(err);
      }
    },
    [user, deleteModelMutation, models, setModels]
  );

  return {
    models,
    setModels,
    createModel,
    updateModel,
    deleteModel,
  };
};

export const useModelState = <K extends keyof IModel>(
  modelId: string,
  key: K
) => {
  const { models, updateModel } = useModels();

  const model = models.find((m) => m.id === modelId);

  // Infer the type of `value` based on the provided `key`
  const value = model?.[key] ?? null;
  const setValue = useCallback(
    (newValue: typeof value, upload = false): Promise<void> | void => {
      return updateModel(modelId)({ [key]: newValue }, upload);
    },
    [key, modelId, updateModel]
  );

  return { value, setValue };
};
export const useToggleFavoriteModel = () => {
  const { models, updateModel } = useModels();

  const favoriteModelMutation = api.models.favoriteModel.useMutation();
  const unfavoriteModelMutation = api.models.unfavoriteModel.useMutation();

  const toggleFavoriteModel = async (modelId: string): Promise<void> => {
    const modelIndex = models.findIndex((model) => model.id === modelId);

    const model = models[modelIndex];

    if (!model) {
      return;
    }

    const updatedModel = {
      ...models[modelIndex],
      isFavorited: !model.isFavorited,
    };
    updateModel(modelId)(updatedModel);

    if (updatedModel.isFavorited) {
      await favoriteModelMutation.mutateAsync({ modelId });
    } else {
      await unfavoriteModelMutation.mutateAsync({ modelId });
    }
  };

  return toggleFavoriteModel;
};

export const useModelDataset = (modelId: string) => {
  const { data } = useSession();

  const { models, updateModel } = useModels();

  const model = models.find((model) => model.id === modelId);

  const dataset = useMemo(() => {
    const unsortedDataset = model?.dataset ?? [];
    return unsortedDataset.sort((a, b) => {
      if (a.quality && b.quality) {
        if (a.quality === b.quality) {
          return b.path.localeCompare(a.path);
        }
        return b.quality - a.quality;
      }

      if (a.quality) {
        return -1;
      }

      if (b.quality) {
        return 1;
      }

      return b.path.localeCompare(a.path);
    });
  }, [model?.dataset]);

  const { mutateAsync: createModelDatasetRows } =
    api.models.createModelDatasetRows.useMutation();

  const { uploadFiles, isUploadingFiles, uploadProgress, deleteFiles } =
    useStorage();

  const { mutateAsync: deleteModelDatasetRows } =
    api.models.deleteModelDatasetRows.useMutation();

  const uploadDatasetFiles = useCallback(
    async (files: IFileUpload[]) => {
      if (!data || !model) return;

      const datasetFiles: IModel["dataset"] = files.map((file) => {
        return {
          id: uuid(),
          path: file.path,
          modelId: model.id,
          userId: data.user.id,
          quality: null,
          createdAt: new Date(),
        };
      });

      const updatedDataset = [...dataset, ...datasetFiles];

      await uploadFiles(files);
      updateModel(model.id)({ dataset: updatedDataset });
      await createModelDatasetRows({ datasetRows: datasetFiles });
    },
    [createModelDatasetRows, data, dataset, model, updateModel, uploadFiles]
  );

  const removeDatasetFiles = useCallback(
    async (datasetFileIds: string[], upload = false) => {
      if (!model) {
        return;
      }

      const updatedDataset = dataset.filter(
        (datasetFile) => !datasetFileIds.includes(datasetFile.id)
      );

      updateModel(model.id)({ dataset: updatedDataset });

      if (upload) {
        await deleteModelDatasetRows({ datasetRowIds: datasetFileIds });

        const filesToDelete = datasetFileIds.map((datasetFileId) => {
          const datasetFile = dataset.find(
            (datasetFile) => datasetFile.id === datasetFileId
          );

          return {
            bucket: Buckets.AUDIO_DATASETS,
            path: datasetFile?.path ?? "",
          };
        });
        deleteFiles(filesToDelete);
      }
    },
    [dataset, deleteFiles, deleteModelDatasetRows, model, updateModel]
  );

  return {
    dataset,
    uploadDatasetFiles,
    removeDatasetFiles,
    isUploadingFiles,
    uploadProgress,
  };
};

export const useTrainModel = () => {
  const router = useRouter();

  const { updateModel } = useModels();
  const { sendToastMessage } = useToastContext();

  const trainModelMutation = api.models.trainModel.useMutation();

  const trainModel = useCallback(
    async (modelId: string, useHighFidelity = true) => {
      updateModel(modelId)({ status: ModelStatusTypes.training }, true);

      try {
        await trainModelMutation.mutateAsync({ modelId, useHighFidelity });
        if (router.pathname !== FEATURE_ROUTES.voices) {
          router.push(FEATURE_ROUTES.voices);
        }
      } catch (err) {
        // A tRPC rejection here is a gate saying no — a lapsed subscription, the retrain
        // limit, too many trainings already running — not the training pipeline breaking.
        // Recording those as `failed` is what made 14 cancelled subscribers look like 14
        // pipeline failures, every one of them lapsed before the row was even created.
        // Only gate codes count. models.ts throws INTERNAL_SERVER_ERROR when the Modal
        // training call fails, so treating every tRPC code as a refusal would hide the
        // real failures instead of the fake ones.
        const refused = isRefusalError(err);
        updateModel(modelId)(
          {
            status: refused
              ? ModelStatusTypes.refused
              : ModelStatusTypes.failed,
          },
          true
        );
        const message =
          err instanceof Error
            ? err.message
            : "Voice training failed. Please try again.";
        sendToastMessage(message, { severity: "error" });
        throw err;
      }
    },
    [router, sendToastMessage, trainModelMutation, updateModel]
  );

  return trainModel;
};
